import type { Project } from "../types";

const ANDROID = { en: "Android", ru: "Android" };
const BACKEND = { en: "Backend", ru: "Бэкенд" };

/**
 * Portfolio projects, newest first.
 * Edit text / links here — the UI is generated from this list.
 */
export const projects: Project[] = [
  {
    id: "videostreamplayer",
    title: "VideoStreamPlayerTestApp",
    year: "2019",
    platform: ANDROID,
    summary: {
      en: "Android player that streams online video from a direct file link.",
      ru: "Android-плеер для онлайн-видео по прямой ссылке на файл.",
    },
    description: {
      en: [
        "Android application that lets you watch online video by providing a direct link to the video file. Targets API 21+ (Android 5.0+).",
        "Implemented in both Kotlin and Java (separate branch) as a study of the two stacks side by side.",
      ],
      ru: [
        "Android-приложение, которое проигрывает онлайн-видео по прямой ссылке на видеофайл. Минимальная версия — API 21+ (Android 5.0+).",
        "Реализовано и на Kotlin, и на Java (в отдельной ветке) — чтобы сравнить оба стека бок о бок.",
      ],
    },
    tags: ["Kotlin", "Java", "ExoPlayer", "Android 5.0+"],
    category: "open-source",
    image: "images/examples/videostreamplayertestapp/videostreamplayertestapp.png",
    gallery: [
      "images/examples/videostreamplayertestapp/videostreamplayertestapp1.png",
      "images/examples/videostreamplayertestapp/videostreamplayertestapp2.png",
      "images/examples/videostreamplayertestapp/videostreamplayertestapp3.png",
      "images/examples/videostreamplayertestapp/videostreamplayertestapp4.png",
    ],
    links: [
      {
        label: "GitHub — Kotlin",
        url: "https://github.com/LordDarthArt/VideoStreamPlayerTestApp",
      },
      {
        label: "GitHub — Java",
        url: "https://github.com/LordDarthArt/VideoStreamPlayerTestApp/tree/javabranch",
      },
    ],
  },
  {
    id: "justdoitlist",
    title: "JustDoItList",
    year: "2019",
    platform: ANDROID,
    summary: {
      en: "To-do app with reminders, backed by Firebase Cloud Firestore & Auth.",
      ru: "Список задач с напоминаниями на Firebase Cloud Firestore и Auth.",
    },
    description: {
      en: [
        "Android app for creating and managing tasks. Each task can carry a reminder that fires 5 minutes, 30 minutes, 1 hour or 1 day before it is due.",
        "Uses Firebase Cloud Firestore as the task database and Firebase Auth so every user keeps a personal to-do list. Multi-activity, multi-fragment architecture. API 21+ (Android 5.0+).",
      ],
      ru: [
        "Android-приложение для создания и ведения задач. К каждой задаче можно прикрепить напоминание за 5 минут, 30 минут, 1 час или 1 день до срока.",
        "База задач — Firebase Cloud Firestore, авторизация — Firebase Auth, так что у каждого пользователя свой список. Архитектура из нескольких активити и фрагментов. API 21+ (Android 5.0+).",
      ],
    },
    tags: ["Kotlin", "Firebase", "Cloud Firestore", "Firebase Auth"],
    category: "open-source",
    image: "images/examples/justdo.png",
    links: [],
  },
  {
    id: "newstestapp",
    title: "NewsTestApp",
    year: "2019",
    platform: ANDROID,
    summary: {
      en: "News reader pulling articles from a REST API (Lenta.ru).",
      ru: "Читалка новостей через REST API (Lenta.ru).",
    },
    description: {
      en: [
        "Android app that reads news from an external server over REST, using the Lenta.ru API. Written entirely in Kotlin.",
        "Loads remote images with the Ion library and handles the REST layer with Retrofit and Gson. API 21+ (Android 5.0+).",
      ],
      ru: [
        "Android-приложение, которое читает новости с внешнего сервера по REST через API Lenta.ru. Полностью на Kotlin.",
        "Загрузка удалённых изображений — библиотека Ion, REST-слой — Retrofit и Gson. API 21+ (Android 5.0+).",
      ],
    },
    tags: ["Kotlin", "Retrofit", "Gson", "Ion", "REST"],
    category: "open-source",
    image: "images/examples/newstestapp.png",
    links: [
      { label: "GitHub", url: "https://github.com/LordDarthArt/NewsTestApp" },
    ],
  },
  {
    id: "rxnewstestapp",
    title: "RxNewsApp",
    year: "2019",
    platform: ANDROID,
    summary: {
      en: "News reader built on Retrofit2, Gson & OkHttp3 with bottom nav.",
      ru: "Читалка новостей на Retrofit2, Gson и OkHttp3 с нижней навигацией.",
    },
    description: {
      en: [
        "Android app that reads news from an external server over REST — this time with Retrofit2, Gson and OkHttp3.",
        "Navigation is driven by a BottomNavigation that swaps fragments; the main fragment hosts a TabLayout with a pager. API 21+ (Android 5.0+).",
      ],
      ru: [
        "Android-приложение, читающее новости с внешнего сервера по REST — на этот раз на Retrofit2, Gson и OkHttp3.",
        "Навигация построена на BottomNavigation с переключением фрагментов; в главном фрагменте — TabLayout с пейджером. API 21+ (Android 5.0+).",
      ],
    },
    tags: ["Kotlin", "Retrofit2", "OkHttp3", "Gson", "RxJava"],
    category: "open-source",
    image: "images/examples/rxnewstestapp.png",
    links: [
      { label: "GitHub", url: "https://github.com/LordDarthArt/RxNewsTestApp" },
    ],
  },
  {
    id: "vkauthtestapp",
    title: "VKAuthTestApp",
    year: "2019",
    platform: ANDROID,
    summary: {
      en: "VK login that lists your first five friends alphabetically.",
      ru: "Вход через VK и список первых пяти друзей по алфавиту.",
    },
    description: {
      en: [
        "Android app that uses the VK Android SDK to authorise a VK user and fetch their top five friends by alphabet.",
        "The VKAccessToken is persisted, so the app remembers the session and you do not have to re-authorise on every launch. API 21+ (Android 5.0+).",
      ],
      ru: [
        "Android-приложение на VK Android SDK: авторизует пользователя VK и показывает пятерых друзей по алфавиту.",
        "VKAccessToken сохраняется, поэтому приложение помнит сессию — авторизоваться заново при каждом запуске не нужно. API 21+ (Android 5.0+).",
      ],
    },
    tags: ["Kotlin", "VK Android SDK", "OAuth"],
    category: "open-source",
    image: "images/examples/vkauthtestapp.png",
    links: [
      { label: "GitHub", url: "https://github.com/LordDarthArt/VKAuthTestApp" },
    ],
  },
  {
    id: "accurateweather",
    title: "AccurateWeatherTestApp",
    year: "2019",
    platform: ANDROID,
    summary: {
      en: "Weather forecasts for selected cities via the Yandex.Weather API.",
      ru: "Прогноз погоды по выбранным городам через API Яндекс.Погоды.",
    },
    description: {
      en: [
        "Android app that fetches weather forecasts for selected cities from Yandex.Weather over REST.",
        "A fork of WeatherTestApp with several improvements. API 21+ (Android 5.0+).",
      ],
      ru: [
        "Android-приложение, которое получает прогноз погоды по выбранным городам из Яндекс.Погоды по REST.",
        "Форк WeatherTestApp с рядом улучшений. API 21+ (Android 5.0+).",
      ],
    },
    tags: ["Kotlin", "Yandex.Weather", "REST"],
    category: "open-source",
    image: "images/examples/accurateweathertestapp.png",
    links: [
      {
        label: "GitHub",
        url: "https://github.com/LordDarthArt/AccurateWeatherTestApp",
      },
      {
        label: "WeatherTestApp",
        url: "https://github.com/LordDarthArt/WeatherTestApp",
      },
    ],
  },
  {
    id: "pushovertestapp",
    title: "PushOverTestApp",
    year: "2018",
    platform: ANDROID,
    summary: {
      en: "Sends push notifications to a user's devices via the PushOver API.",
      ru: "Отправляет push-уведомления на устройства пользователя через PushOver API.",
    },
    description: {
      en: [
        "Android app that uses the PushOver API to send notifications to a target user's devices. If the notification text is a link, it is attached as a notification link.",
        "You can set your own title and body, target a specific user key and device, schedule a pending notification, or even scan a QR code and send the result as a notification. The recipient needs the PushOver app installed. API 21+ (Android 5.0+).",
      ],
      ru: [
        "Android-приложение на PushOver API: отправляет уведомления на устройства заданного пользователя. Если текст уведомления — ссылка, она прикрепляется к уведомлению.",
        "Можно задать свой заголовок и текст, выбрать ключ пользователя и устройство, создать отложенное уведомление и даже отсканировать QR-код и отправить результат уведомлением. У получателя должно быть установлено приложение PushOver. API 21+ (Android 5.0+).",
      ],
    },
    tags: ["Kotlin", "PushOver API", "QR", "Notifications"],
    category: "open-source",
    image: "images/examples/pushovertestapp.png",
    links: [
      { label: "GitHub", url: "https://github.com/LordDarthArt/PushOverTestApp" },
    ],
  },
  {
    id: "yandexweather",
    title: "YandexWeatherTestApp",
    year: "2018",
    platform: ANDROID,
    summary: {
      en: "Yandex Weather forecasts with per-user API keys kept in Firestore.",
      ru: "Прогнозы Яндекс.Погоды с индивидуальными API-ключами в Firestore.",
    },
    description: {
      en: [
        "Android app that uses the Yandex Weather API to fetch forecast data. To keep the API key private, it is stored per authorised user in Cloud Firestore rather than in the app.",
        "Authorisation is handled via a Google account; on first launch you supply your Yandex API key, otherwise the app will not run. The UI is a single main activity that loads different fragments.",
      ],
      ru: [
        "Android-приложение на API Яндекс.Погоды. Чтобы не хранить ключ в приложении, он лежит в Cloud Firestore отдельно для каждого авторизованного пользователя.",
        "Авторизация — через аккаунт Google; при первом запуске нужно указать свой ключ Яндекс API, иначе приложение не работает. Интерфейс — одна главная активити, подгружающая разные фрагменты.",
      ],
    },
    tags: ["Kotlin", "Yandex.Weather", "Cloud Firestore", "Google Auth"],
    category: "open-source",
    image: "images/examples/yandexweathertestapp.png",
    links: [
      {
        label: "GitHub",
        url: "https://github.com/LordDarthArt/YandexWeatherTestApp",
      },
    ],
  },
  {
    id: "springbackend",
    title: "TestBackendAppUsingSpring",
    year: "2018",
    platform: BACKEND,
    summary: {
      en: "RESTful web service on Spring Boot, Spring MVC, Hibernate & Tomcat.",
      ru: "RESTful-сервис на Spring Boot, Spring MVC, Hibernate и Tomcat.",
    },
    description: {
      en: [
        "A RESTful backend built with Spring Boot, Spring MVC, Hibernate and Tomcat on top of an H2 database.",
        "A MiniUniversity table stores students, their ages, groups and teachers.",
      ],
      ru: [
        "RESTful-бэкенд на Spring Boot, Spring MVC, Hibernate и Tomcat поверх базы H2.",
        "Таблица MiniUniversity хранит студентов, их возраст, группы и преподавателей.",
      ],
    },
    features: {
      en: [
        "Create a group",
        "List all students",
        "Get a student by ID",
        "Update a group's teacher",
        "List a teacher's groups",
        "List a group's students",
        "Get a student's teacher",
      ],
      ru: [
        "Создать группу",
        "Получить список всех студентов",
        "Получить студента по ID",
        "Сменить преподавателя группы",
        "Получить группы преподавателя",
        "Получить студентов группы",
        "Получить преподавателя студента",
      ],
    },
    tags: ["Java", "Spring Boot", "Spring MVC", "Hibernate", "H2"],
    category: "open-source",
    image: "images/examples/testbackendappusingspring.png",
    links: [
      {
        label: "GitHub",
        url: "https://github.com/LordDarthArt/TestBackendAppUsingSpring",
      },
    ],
  },
  {
    id: "vkapitest",
    title: "VK API Test",
    year: "2018",
    platform: ANDROID,
    summary: {
      en: "Reads posts from a VK user's wall through the VK REST API.",
      ru: "Читает записи со стены пользователя VK через REST API VK.",
    },
    description: {
      en: [
        "Android app that uses the VK API to fetch posts from a user's wall. You supply the target profile ID and a post count (up to 100).",
        "Posts with only an image and no text are skipped. If the wall is protected by privacy settings, an error message is shown instead of the post list.",
      ],
      ru: [
        "Android-приложение на VK API: получает записи со стены пользователя. Указываешь ID профиля и число записей (до 100).",
        "Записи без текста, только с картинкой, пропускаются. Если стена закрыта настройками приватности, вместо списка показывается сообщение об ошибке.",
      ],
    },
    tags: ["Java", "VK API", "REST"],
    category: "open-source",
    image: "images/examples/vkapitest.png",
    links: [
      { label: "GitHub", url: "https://github.com/LordDarthArt/VK_API_Test" },
    ],
  },
  {
    id: "weathertestapp",
    title: "WeatherTestApp",
    year: "2018",
    platform: ANDROID,
    summary: {
      en: "Weather forecasts for selected cities, powered by Yahoo.",
      ru: "Прогноз погоды по выбранным городам на данных Yahoo.",
    },
    description: {
      en: [
        "Android app that fetches weather forecasts for selected cities from Yahoo over a REST API.",
      ],
      ru: [
        "Android-приложение, получающее прогноз погоды по выбранным городам из Yahoo по REST API.",
      ],
    },
    tags: ["Java", "Yahoo Weather", "REST"],
    category: "open-source",
    image: "images/examples/weathertestapp.png",
    links: [
      { label: "GitHub", url: "https://github.com/LordDarthArt/WeatherTestApp" },
    ],
  },
  {
    id: "mybuhg",
    title: "MyBuhg",
    year: "2018",
    platform: ANDROID,
    summary: {
      en: "Commercial Android client for the mybuhg.ru accounting service.",
      ru: "Коммерческий Android-клиент для сервиса учёта mybuhg.ru.",
    },
    description: {
      en: [
        "Android client for mybuhg.ru, targeting devices with a 4.7″ screen or larger. The app models organisations and the users that belong to them.",
        "Sign-in uses the Google account on the device. On first login a document is created in the Cloud Firestore users collection under the user's UID, with name and verification fields; verification and organisation assignment are done by a DB administrator.",
        "New invoices are added by scanning receipts and emailing them to the administrator (photos are converted to compact PDFs) or by picking existing images / PDFs from device storage. The main screen shows the user's invoices for the last 30 days; History supports filtering by period, bank, counterparty and more. Uses Firebase Cloud Firestore (with access-control rules) and Google Cloud Messaging. Delivered to the lead project manager.",
      ],
      ru: [
        "Android-клиент для mybuhg.ru под устройства с экраном 4.7″ и больше. В приложении есть организации и пользователи, которые к ним относятся.",
        "Вход — через аккаунт Google на устройстве. При первом входе в коллекции users в Cloud Firestore создаётся документ под UID пользователя с полями имени и верификации; верификацию и привязку к организации делает администратор БД.",
        "Новые счета добавляются сканированием чеков с отправкой на почту администратора (фото конвертируются в компактный PDF) либо выбором готовых изображений/PDF из памяти устройства. На главном экране — счета пользователя за последние 30 дней; в истории — фильтры по периоду, банку, контрагенту и т.д. Используются Firebase Cloud Firestore (с правилами доступа) и Google Cloud Messaging. Сдано ведущему проект-менеджеру.",
      ],
    },
    tags: ["Java", "Firebase", "Cloud Firestore", "GCM", "iText / PDF"],
    category: "commercial",
    image: "images/examples/mybuhg.png",
    links: [],
  },
  {
    id: "driverstasks",
    title: "DriversTasks",
    year: "2018",
    platform: ANDROID,
    summary: {
      en: "Commercial app automating an enterprise logistics department.",
      ru: "Коммерческое приложение для автоматизации отдела логистики предприятия.",
    },
    description: {
      en: [
        "Android app that automates an enterprise logistics department, used by the company's drivers. A driver signs in and receives the list of tasks for the day; each task can be started or postponed, and the status flows to an SQL server (1C Enterprise 8.3) and on to the dispatcher.",
        "For quality control, an authorised driver's coordinates are sent to the server every 15 minutes. Tasks are grouped by status — New, Running, Completed, Postponed — and a new task triggers a Google Cloud Message push generated by the server via a REST request.",
        "The app combines a local SQLite database, the enterprise's main server and Firebase. Data sync runs in AsyncTask, where GET requests and JSON parsing reconcile with the local SQLite store. Authorisation adds a value to the request header. In production use.",
      ],
      ru: [
        "Android-приложение для автоматизации отдела логистики предприятия, для водителей компании. Водитель входит и получает список задач на день; задачу можно начать или отложить, а статус уходит на SQL-сервер (1С:Предприятие 8.3) и далее диспетчеру.",
        "Для контроля качества координаты авторизованного водителя отправляются на сервер каждые 15 минут. Задачи сгруппированы по статусу — новые, в работе, выполненные, отложенные, — а новая задача триггерит push через Google Cloud Message, сгенерированный сервером REST-запросом.",
        "Приложение объединяет локальную базу SQLite, основной сервер предприятия и Firebase. Синхронизация идёт в AsyncTask, где GET-запросы и разбор JSON сверяются с локальной SQLite. Авторизация добавляет значение в заголовок запроса. В продакшене.",
      ],
    },
    tags: ["Java", "SQLite", "1C Enterprise", "Firebase", "GCM"],
    category: "commercial",
    image: "images/examples/driverstasks.png",
    links: [],
  },
  {
    id: "businessschedule",
    title: "BusinessSchedule",
    year: "2017",
    platform: ANDROID,
    summary: {
      en: "Pilot app showing meeting-room events on an Android tablet.",
      ru: "Пилот: показывает события переговорной на Android-планшете.",
    },
    description: {
      en: [
        "Pilot project: an Android app that displays the meeting-room event list on a 1024×768 tablet screen — split into next few hours, current day and current week — for enterprises.",
        "Events are read from the Firebase Realtime Database; a login/password pair registered in the Firebase project handles authorisation. Delivered to the lead project manager.",
      ],
      ru: [
        "Пилотный проект: Android-приложение, показывающее список событий переговорной на экране планшета 1024×768 — разбитый на ближайшие часы, текущий день и текущую неделю — для предприятий.",
        "События читаются из Firebase Realtime Database; авторизация — по паре логин/пароль, зарегистрированной в проекте Firebase. Сдано ведущему проект-менеджеру.",
      ],
    },
    tags: ["Java", "Firebase", "Realtime Database"],
    category: "commercial",
    image: "images/examples/businessschedule.png",
    links: [],
  },
];
